import type { Aspect, Design, Widget } from '@noorview/core';
import { WIDGETS, clampRect, findSpot, isFree, keepShape, minSize, newId } from '@noorview/core';
import { create } from 'zustand';

/*
 * Copy and paste widgets, shared by the web app and the iPhone app. Copied widgets are kept on this device (so they
 * can be pasted on another screen, or later), and a group pasted onto an empty area keeps its layout.
 */

export interface Clip {
  v: 1;
  /** The grid they were copied from, so they keep their proportions on a screen of another shape. */
  cols: number;
  rows: number;
  aspect: Aspect;
  widgets: Widget[];
  /** The screen they came from. */
  from: string;
  at: number;
}

const KEY = 'noorview.studio.clipboard';

function load(): Clip | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Clip;
    if (c?.v !== 1 || !Array.isArray(c.widgets) || !c.widgets.length || !(c.cols > 0) || !(c.rows > 0)) return null;
    const widgets = c.widgets.filter((w) => w && typeof w === 'object' && WIDGETS[w.type as keyof typeof WIDGETS]);
    return widgets.length ? { ...c, widgets } : null;
  } catch { return null; }
}

/** What's been copied (if anything), plus the widgets picked for copying several at once. */
export const useClipboard = create<{ clip: Clip | null; picking: boolean; picked: string[] }>(() => ({ clip: null, picking: false, picked: [] }));

let loaded = false;
/** The copied widgets, read from this device the first time they're needed. */
export function getClip(): Clip | null {
  if (!loaded) { loaded = true; useClipboard.setState({ clip: load() }); }
  return useClipboard.getState().clip;
}

/** Picking several widgets: on, off, and adding or removing one. */
export const setPicking = (on: boolean, ids: string[] = []) => useClipboard.setState({ picking: on, picked: on ? ids : [] });
export const togglePicked = (id: string) => useClipboard.setState((s) => ({ picked: s.picked.includes(id) ? s.picked.filter((x) => x !== id) : [...s.picked, id] }));

/** Copies these widgets (top to bottom, left to right). Returns how many were copied. */
export function copyWidgets(d: Design, widgets: readonly Widget[], from: string): number {
  if (!widgets.length) return 0;
  const sorted = [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);
  const clip: Clip = { v: 1, cols: d.cols, rows: d.rows, aspect: d.aspect, widgets: structuredClone(sorted), from, at: Date.now() };
  try { localStorage.setItem(KEY, JSON.stringify(clip)); } catch { /* still pasteable until the page closes */ }
  loaded = true;
  useClipboard.setState({ clip });
  return sorted.length;
}

/**
 * Where the copied widgets go on `d`: each keeps its place (scaled to this grid) when that's free, otherwise the
 * nearest free space of its size, otherwise the largest size that still fits. Widgets with no room are left out.
 */
export function pasteWidgets(d: Design, clip: Clip): { added: Widget[]; skipped: number } {
  const sx = d.cols / clip.cols, sy = d.rows / clip.rows;
  const placed: Widget[] = [...d.widgets];
  const added: Widget[] = [];
  let skipped = 0;
  for (const src of clip.widgets) {
    const base: Widget = { ...structuredClone(src), id: newId(), locked: false, hidden: false };
    const min = minSize(base, d.cols, d.rows);
    const want = clampRect({ x: src.x * sx, y: src.y * sy, w: Math.max(1, src.w * sx), h: Math.max(1, src.h * sy) }, d.cols, d.rows, min);
    let r = isFree(placed, want) ? want : findSpot(placed, want.w, want.h, d.cols, d.rows);
    for (let h = want.h; !r && h >= min.h; h--) for (let w = want.w; !r && w >= min.w; w--) r = findSpot(placed, w, h, d.cols, d.rows);
    if (!r) { skipped++; continue; }
    // Fixed-shape widgets (posters) keep their shape, as long as that still fits.
    const shaped = keepShape(base, r, d, 'w');
    const w: Widget = { ...base, ...(isFree(placed, shaped) ? shaped : r) };
    placed.push(w);
    added.push(w);
  }
  return { added, skipped };
}

/** "3 widgets", "Prayer Times". */
export const clipLabel = (clip: Clip) => (clip.widgets.length === 1 ? (clip.widgets[0]!.name || WIDGETS[clip.widgets[0]!.type].label) : `${clip.widgets.length} widgets`);
